import {
  FabricObject,
  FabricObjectProps,
  ObjectEvents,
  SerializedObjectProps,
  Rect,
} from "fabric";
import Tool, { ITool } from "./Tool";
import CanvasUtils from "../utils/CanvasUtils";
import ImageObject from "../objects/ImageObject";

export type TransformOptions = object;

class TransformTool extends Tool implements ITool<TransformOptions> {
  originalImage: FabricObject<
    Partial<FabricObjectProps>,
    SerializedObjectProps,
    ObjectEvents
  > | null = null;

  cropRect: Rect | null = null;

  activate(): void {
    if (!this.canvas) return;

    [this.originalImage] = this.canvas.getObjects("image");

    this.createCropRect();

    CanvasUtils.updateCanvasCursor(this.canvas, "crosshair");
  }

  createCropRect(): void {
    if (!this.canvas || !this.originalImage) return;

    if (this.cropRect) {
      this.canvas.remove(this.cropRect);
      this.cropRect = null;
    }

    const { width, height } = this.canvas;

    const scaleFactor = Math.min(width, height) / 900;
    const strokeWidth = Math.max(2, 5 * scaleFactor);
    const cornerSize = Math.max(5, 17 * scaleFactor);

    this.cropRect = new Rect({
      originX: "left",
      originY: "top",
      left: 0,
      top: 0,
      width: width - strokeWidth,
      height: height - strokeWidth,
      fill: "rgba(0,0,0,0.2)",
      stroke: "rgb(255,255,255)",
      strokeWidth,
      hasBorders: true,
      hasControls: true,
      selectable: true,
      objectCaching: false,
      lockRotation: true,
      cornerSize,
      transparentCorners: false,
      cornerColor: "rgb(255,255,255)",
    });

    this.canvas.add(this.cropRect);
    this.canvas.setActiveObject(this.cropRect);
  }

  async crop(): Promise<{ imgWidth: number; imgHeight: number } | null> {
    if (!this.canvas || !this.cropRect) return null;

    const rect = this.cropRect;

    const boundingRect = rect.getBoundingRect();

    const strokeWidth = rect.strokeWidth || 0;

    let left = (boundingRect.left ?? 0) + strokeWidth;
    let top = (boundingRect.top ?? 0) + strokeWidth;
    let width = (boundingRect.width ?? 0) - strokeWidth;
    let height = (boundingRect.height ?? 0) - strokeWidth;

    const canvasWidth = this.canvas.getWidth();
    const canvasHeight = this.canvas.getHeight();

    if (
      Math.abs(boundingRect.left) < 1 &&
      Math.abs(boundingRect.top) < 1 &&
      Math.abs(boundingRect.width - canvasWidth) < 1 &&
      Math.abs(boundingRect.height - canvasHeight) < 1
    ) {
      return null;
    }

    if (left < 0) {
      width += left;
      left = 0;
    }
    if (top < 0) {
      height += top;
      top = 0;
    }
    if (left + width > canvasWidth) {
      width = canvasWidth - left;
    }
    if (top + height > canvasHeight) {
      height = canvasHeight - top;
    }

    if (width <= 0 || height <= 0) return null;

    this.canvas.remove(rect);

    const dataUrl = this.canvas.toDataURL({
      left,
      top,
      width,
      height,
      format: "png",
      multiplier: 1,
    });

    const image = new ImageObject(dataUrl);

    try {
      await image.init();

      if (image.object) {
        this.canvas.clear();
        this.canvas.add(image.object);
      }
    } catch {
      return null;
    }

    return { imgWidth: width, imgHeight: height };
  }

  rotate(): { imgWidth: number; imgHeight: number } | null {
    if (!this.originalImage || !this.canvas) return null;

    let angle = (this.originalImage.angle ?? 0) - 90;
    if (angle < 0) angle += 360;

    this.originalImage.set("angle", angle);

    const width = this.originalImage.getScaledWidth();
    const height = this.originalImage.getScaledHeight();

    const isRotated = angle % 180 !== 0;

    this.originalImage.set({
      originX: "center",
      originY: "center",
      left: (isRotated ? height : width) / 2,
      top: (isRotated ? width : height) / 2,
    });

    this.originalImage.setCoords();
    this.canvas.renderAll();

    return isRotated
      ? { imgWidth: height, imgHeight: width }
      : { imgWidth: width, imgHeight: height };
  }

  flip() {
    if (!this.canvas || !this.originalImage) return;

    const angle = (this.originalImage.angle || 0) % 360;

    if (angle === 0 || angle === 180) {
      const current = this.originalImage.flipX || false;
      this.originalImage.set("flipX", !current);
    } else if (angle === 90 || angle === 270) {
      const current = this.originalImage.flipY || false;
      this.originalImage.set("flipY", !current);
    }

    this.originalImage.setCoords();
    this.canvas.renderAll();
  }

  async deactivate() {
    if (this.canvas && this.cropRect) {
      this.canvas.remove(this.cropRect);
      this.cropRect = null;
    }
    this.canvas = null;
    this.originalImage = null;
  }
}

export default TransformTool;
